// SupportTicketsTab.tsx — Admin Support tab (#233)
// 4 sub-tabs: Queue, Contact Inquiries, Canned Responses, SLA Dashboard
import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Button, Card, CardContent, Alert, Chip,
    CircularProgress, TextField, Select, MenuItem, FormControl,
    InputLabel, Table, TableBody, TableHead, TableRow, TableCell,
    TableContainer, Paper, Drawer, Divider, IconButton, Tabs, Tab,
    Dialog, DialogTitle, DialogContent, DialogActions, Tooltip,
} from '@mui/material';
import {
    ConfirmationNumber, Mail, Reply, Schedule, Close,
    Warning, CheckCircle, Refresh, Add, Delete,
} from '@mui/icons-material';
import { apiRequest } from '../../utils/api';

const TEAL = '#2D6E6A';
// ── Types ─────────────────────────────────────────────────────────────────────

interface Ticket {
    ticket_id: string;
    subject: string;
    status: 'open' | 'pending' | 'resolved' | 'closed';
    priority: 'urgent' | 'high' | 'normal' | 'low';
    category: string | null;
    sla_target: string | null;
    sla_breached: boolean;
    first_responded_at: string | null;
    created_at: string;
    updated_at: string;
    user_email: string;
    user_name: string;
    assigned_to_name: string | null;
    message_count: number;
}

interface TicketMessage {
    message_id: string;
    author_role: string;
    author_name: string;
    author_email: string;
    body: string;
    is_internal_note: boolean;
    created_at: string;
}

interface TicketDetail extends Ticket {
    assigned_to_user_id: string | null;
}

interface CannedResponse {
    response_id: string;
    title: string;
    body: string;
    category: string | null;
    created_by_name: string | null;
    created_at: string;
}

interface Inquiry {
    inquiry_id: string;
    name: string;
    email: string;
    subject: string;
    message: string;
    status: 'new' | 'read' | 'replied' | 'archived';
    admin_notes: string | null;
    created_at: string;
    updated_at: string;
}

interface SlaSummary {
    open_count: string;
    breached_count: string;
    urgent_open: string;
    high_open: string;
    avg_first_response_hours: string | null;
    awaiting_first_response: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const priorityColor = (p: string): 'error' | 'warning' | 'default' | 'info' => {
    if (p === 'urgent') return 'error';
    if (p === 'high') return 'warning';
    if (p === 'normal') return 'default';
    return 'info';
};

const statusColor = (s: string): 'success' | 'warning' | 'default' | 'error' => {
    if (s === 'resolved' || s === 'closed') return 'success';
    if (s === 'pending') return 'warning';
    if (s === 'open') return 'default';
    return 'default';
};

const fmtDate = (d: string | null) => d ? new Date(d).toLocaleString() : '—';
const fmtDateShort = (d: string) => new Date(d).toLocaleDateString();

// ── Sub-tab: Queue ─────────────────────────────────────────────────────────────

const QueueTab: React.FC = () => {
    const [tickets, setTickets] = useState<Ticket[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterPriority, setFilterPriority] = useState('');
    const [filterCategory, setFilterCategory] = useState('');
    const [selectedTicket, setSelectedTicket] = useState<TicketDetail | null>(null);
    const [messages, setMessages] = useState<TicketMessage[]>([]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [drawerLoading, setDrawerLoading] = useState(false);
    const [replyBody, setReplyBody] = useState('');
    const [isInternalNote, setIsInternalNote] = useState(false);
    const [replySending, setReplySending] = useState(false);
    const [replyError, setReplyError] = useState('');
    const [updateLoading, setUpdateLoading] = useState(false);

    const loadTickets = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams();
            if (filterStatus) params.append('status', filterStatus);
            if (filterPriority) params.append('priority', filterPriority);
            if (filterCategory) params.append('category', filterCategory);
            const res = await apiRequest(`/admin/support/tickets?${params.toString()}`);
            const data = await res.json();
            if (data.success) setTickets(data.tickets);
            else setError(data.error || 'Failed to load tickets');
        } catch {
            setError('Network error loading tickets');
        } finally {
            setLoading(false);
        }
    }, [filterStatus, filterPriority, filterCategory]);

    useEffect(() => { loadTickets(); }, [loadTickets]);

    const openTicket = async (ticketId: string) => {
        setDrawerOpen(true);
        setDrawerLoading(true);
        setReplyBody('');
        setReplyError('');
        try {
            const res = await apiRequest(`/admin/support/tickets/${ticketId}`);
            const data = await res.json();
            if (data.success) {
                setSelectedTicket(data.ticket);
                setMessages(data.messages);
            }
        } catch {
            setError('Failed to load ticket detail');
        } finally {
            setDrawerLoading(false);
        }
    };

    const sendReply = async () => {
        if (!selectedTicket || !replyBody.trim()) return;
        setReplySending(true);
        setReplyError('');
        try {
            const res = await apiRequest(`/admin/support/tickets/${selectedTicket.ticket_id}/messages`, {
                method: 'POST',
                body: JSON.stringify({ message: replyBody.trim(), is_internal_note: isInternalNote }),
            });
            const data = await res.json();
            if (data.success) {
                setReplyBody('');
                await openTicket(selectedTicket.ticket_id);
                await loadTickets();
            } else {
                setReplyError(data.error || 'Failed to send reply');
            }
        } catch {
            setReplyError('Network error');
        } finally {
            setReplySending(false);
        }
    };

    const updateTicket = async (field: string, value: string) => {
        if (!selectedTicket) return;
        setUpdateLoading(true);
        try {
            const res = await apiRequest(`/admin/support/tickets/${selectedTicket.ticket_id}`, {
                method: 'PATCH',
                body: JSON.stringify({ [field]: value }),
            });
            const data = await res.json();
            if (data.success) {
                await openTicket(selectedTicket.ticket_id);
                await loadTickets();
            }
        } finally {
            setUpdateLoading(false);
        }
    };

    return (
        <Box>
            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>Status</InputLabel>
                    <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="open">Open</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="resolved">Resolved</MenuItem>
                        <MenuItem value="closed">Closed</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>Priority</InputLabel>
                    <Select value={filterPriority} label="Priority" onChange={e => setFilterPriority(e.target.value)}>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="urgent">Urgent</MenuItem>
                        <MenuItem value="high">High</MenuItem>
                        <MenuItem value="normal">Normal</MenuItem>
                        <MenuItem value="low">Low</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel>Category</InputLabel>
                    <Select value={filterCategory} label="Category" onChange={e => setFilterCategory(e.target.value)}>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="bug">Bug</MenuItem>
                        <MenuItem value="billing">Billing</MenuItem>
                        <MenuItem value="account">Account</MenuItem>
                        <MenuItem value="trip">Trip</MenuItem>
                        <MenuItem value="feature_request">Feature Request</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                    </Select>
                </FormControl>
                <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={loadTickets}>Refresh</Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
            ) : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell><strong>Subject</strong></TableCell>
                                <TableCell><strong>User</strong></TableCell>
                                <TableCell><strong>Priority</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell><strong>SLA</strong></TableCell>
                                <TableCell><strong>Messages</strong></TableCell>
                                <TableCell><strong>Created</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tickets.length === 0 ? (
                                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>No tickets found</TableCell></TableRow>
                            ) : tickets.map(t => (
                                <TableRow
                                    key={t.ticket_id}
                                    hover
                                    onClick={() => openTicket(t.ticket_id)}
                                    sx={{ cursor: 'pointer', backgroundColor: t.sla_breached ? '#fff3f3' : 'inherit' }}
                                >
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={500}>{t.subject}</Typography>
                                        {t.category && <Typography variant="caption" color="text.secondary">{t.category}</Typography>}
                                    </TableCell>
                                    <TableCell><Typography variant="body2">{t.user_name || t.user_email}</Typography></TableCell>
                                    <TableCell><Chip label={t.priority} color={priorityColor(t.priority)} size="small" /></TableCell>
                                    <TableCell><Chip label={t.status} color={statusColor(t.status)} size="small" /></TableCell>
                                    <TableCell>
                                        {t.sla_breached
                                            ? <Chip label="BREACHED" color="error" size="small" icon={<Warning />} />
                                            : t.sla_target
                                            ? <Typography variant="caption">{fmtDate(t.sla_target)}</Typography>
                                            : '—'}
                                    </TableCell>
                                    <TableCell>{t.message_count}</TableCell>
                                    <TableCell><Typography variant="caption">{fmtDateShort(t.created_at)}</Typography></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Ticket Drawer */}
            <Drawer anchor="right" open={drawerOpen} onClose={() => setDrawerOpen(false)}
                PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, p: 3 } }}>
                {drawerLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', pt: 6 }}><CircularProgress /></Box>
                ) : selectedTicket ? (
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                            <Typography variant="h6" fontWeight={600}>{selectedTicket.subject}</Typography>
                            <IconButton onClick={() => setDrawerOpen(false)}><Close /></IconButton>
                        </Box>

                        {/* Ticket meta */}
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                            <Chip label={selectedTicket.priority} color={priorityColor(selectedTicket.priority)} size="small" />
                            <Chip label={selectedTicket.status} color={statusColor(selectedTicket.status)} size="small" />
                            {selectedTicket.sla_breached && <Chip label="SLA BREACHED" color="error" size="small" icon={<Warning />} />}
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            From: <strong>{selectedTicket.user_name}</strong> ({selectedTicket.user_email})
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            SLA target: {fmtDate(selectedTicket.sla_target)}
                        </Typography>

                        {/* Quick actions */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel>Status</InputLabel>
                                <Select value={selectedTicket.status} label="Status"
                                    disabled={updateLoading}
                                    onChange={e => updateTicket('status', e.target.value)}>
                                    <MenuItem value="open">Open</MenuItem>
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="resolved">Resolved</MenuItem>
                                    <MenuItem value="closed">Closed</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl size="small" sx={{ minWidth: 120 }}>
                                <InputLabel>Priority</InputLabel>
                                <Select value={selectedTicket.priority} label="Priority"
                                    disabled={updateLoading}
                                    onChange={e => updateTicket('priority', e.target.value)}>
                                    <MenuItem value="urgent">Urgent</MenuItem>
                                    <MenuItem value="high">High</MenuItem>
                                    <MenuItem value="normal">Normal</MenuItem>
                                    <MenuItem value="low">Low</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        {/* Message thread */}
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Thread</Typography>
                        <Box sx={{ maxHeight: 320, overflowY: 'auto', mb: 2 }}>
                            {messages.map(m => (
                                <Box key={m.message_id} sx={{
                                    mb: 1.5, p: 1.5, borderRadius: 1,
                                    backgroundColor: m.is_internal_note ? '#fff8e1' : m.author_role === 'user' ? '#f5f5f5' : '#e8f5e9',
                                    border: m.is_internal_note ? '1px dashed #f9a825' : '1px solid transparent',
                                }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="caption" fontWeight={600}>
                                            {m.author_name} ({m.author_role}){m.is_internal_note && ' — Internal Note'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">{fmtDate(m.created_at)}</Typography>
                                    </Box>
                                    <Typography variant="body2">{m.body}</Typography>
                                </Box>
                            ))}
                        </Box>

                        <Divider sx={{ mb: 2 }} />

                        {/* Reply */}
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Reply</Typography>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <Button size="small" variant={!isInternalNote ? 'contained' : 'outlined'}
                                onClick={() => setIsInternalNote(false)}
                                sx={{ backgroundColor: !isInternalNote ? TEAL : undefined }}>
                                Public Reply
                            </Button>
                            <Button size="small" variant={isInternalNote ? 'contained' : 'outlined'}
                                color="warning" onClick={() => setIsInternalNote(true)}>
                                Internal Note
                            </Button>
                        </Box>
                        <TextField multiline rows={3} fullWidth size="small"
                            placeholder={isInternalNote ? 'Internal note (not visible to user)...' : 'Reply to user...'}
                            value={replyBody} onChange={e => setReplyBody(e.target.value)}
                            sx={{ mb: 1 }} />
                        {replyError && <Alert severity="error" sx={{ mb: 1 }}>{replyError}</Alert>}
                        <Button variant="contained" fullWidth startIcon={<Reply />}
                            disabled={!replyBody.trim() || replySending}
                            onClick={sendReply}
                            sx={{ backgroundColor: TEAL }}>
                            {replySending ? 'Sending...' : isInternalNote ? 'Save Note' : 'Send Reply'}
                        </Button>
                    </Box>
                ) : null}
            </Drawer>
        </Box>
    );
};

// ── Sub-tab: Contact Inquiries ─────────────────────────────────────────────────

const InquiriesTab: React.FC = () => {
    const [inquiries, setInquiries] = useState<Inquiry[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [filterStatus, setFilterStatus] = useState('new');
    const [selected, setSelected] = useState<Inquiry | null>(null);
    const [adminNotes, setAdminNotes] = useState('');
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const params = filterStatus ? `?status=${filterStatus}` : '';
            const res = await apiRequest(`/admin/support/inquiries${params}`);
            const data = await res.json();
            if (data.success) setInquiries(data.inquiries);
            else setError(data.error || 'Failed to load inquiries');
        } catch {
            setError('Network error');
        } finally {
            setLoading(false);
        }
    }, [filterStatus]);

    useEffect(() => { load(); }, [load]);

    const updateInquiry = async (inquiryId: string, updates: any) => {
        setSaving(true);
        try {
            const res = await apiRequest(`/admin/support/inquiries/${inquiryId}`, {
                method: 'PATCH',
                body: JSON.stringify(updates),
            });
            const data = await res.json();
            if (data.success) {
                await load();
                if (selected?.inquiry_id === inquiryId) {
                    setSelected(data.inquiry);
                }
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 130 }}>
                    <InputLabel>Status</InputLabel>
                    <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="new">New</MenuItem>
                        <MenuItem value="read">Read</MenuItem>
                        <MenuItem value="replied">Replied</MenuItem>
                        <MenuItem value="archived">Archived</MenuItem>
                    </Select>
                </FormControl>
                <Button size="small" variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Box sx={{ display: 'flex', gap: 2, height: 480 }}>
                {/* List */}
                <Box sx={{ flex: 1, overflowY: 'auto' }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress /></Box>
                    ) : inquiries.length === 0 ? (
                        <Typography color="text.secondary" sx={{ p: 2 }}>No inquiries found</Typography>
                    ) : inquiries.map(inq => (
                        <Card key={inq.inquiry_id} variant="outlined" sx={{ mb: 1, cursor: 'pointer',
                            border: selected?.inquiry_id === inq.inquiry_id ? `2px solid ${TEAL}` : undefined }}
                            onClick={() => { setSelected(inq); setAdminNotes(inq.admin_notes || ''); updateInquiry(inq.inquiry_id, { status: inq.status === 'new' ? 'read' : inq.status }); }}>
                            <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" fontWeight={600}>{inq.subject}</Typography>
                                    <Chip label={inq.status} size="small" color={inq.status === 'new' ? 'error' : 'default'} />
                                </Box>
                                <Typography variant="caption" color="text.secondary">{inq.name} — {inq.email}</Typography>
                                <Typography variant="caption" display="block" color="text.secondary">{fmtDateShort(inq.created_at)}</Typography>
                            </CardContent>
                        </Card>
                    ))}
                </Box>

                {/* Detail */}
                <Box sx={{ flex: 1.5, overflowY: 'auto' }}>
                    {selected ? (
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="h6" fontWeight={600} gutterBottom>{selected.subject}</Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                From: <strong>{selected.name}</strong> ({selected.email}) — {fmtDate(selected.created_at)}
                            </Typography>
                            <Paper variant="outlined" sx={{ p: 2, mb: 2, backgroundColor: '#f9f9f9' }}>
                                <Typography variant="body2">{selected.message}</Typography>
                            </Paper>
                            <TextField multiline rows={3} fullWidth size="small" label="Admin Notes"
                                value={adminNotes} onChange={e => setAdminNotes(e.target.value)} sx={{ mb: 1 }} />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button size="small" variant="contained"
                                    sx={{ backgroundColor: TEAL }} disabled={saving}
                                    onClick={() => updateInquiry(selected.inquiry_id, { admin_notes: adminNotes })}>
                                    Save Notes
                                </Button>
                                <Button size="small" variant="outlined"
                                    onClick={() => updateInquiry(selected.inquiry_id, { status: 'replied' })}>
                                    Mark Replied
                                </Button>
                                <Button size="small" variant="outlined" color="inherit"
                                    onClick={() => updateInquiry(selected.inquiry_id, { status: 'archived' })}>
                                    Archive
                                </Button>
                            </Box>
                        </Paper>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            <Typography color="text.secondary">Select an inquiry to view details</Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

// ── Sub-tab: Canned Responses ──────────────────────────────────────────────────

const CannedResponsesTab: React.FC = () => {
    const [responses, setResponses] = useState<CannedResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [newBody, setNewBody] = useState('');
    const [newCategory, setNewCategory] = useState('');
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await apiRequest(`/admin/support/canned-responses`);
            const data = await res.json();
            if (data.success) setResponses(data.responses);
            else setError(data.error || 'Failed to load');
        } catch {
            setError('Network error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const create = async () => {
        if (!newTitle.trim() || !newBody.trim()) return;
        setSaving(true);
        try {
            const res = await apiRequest(`/admin/support/canned-responses`, {
                method: 'POST',
                body: JSON.stringify({ title: newTitle.trim(), body: newBody.trim(), category: newCategory || null }),
            });
            const data = await res.json();
            if (data.success) {
                setDialogOpen(false);
                setNewTitle(''); setNewBody(''); setNewCategory('');
                await load();
            } else {
                setError(data.error || 'Failed to create');
            }
        } finally {
            setSaving(false);
        }
    };

    const deleteResponse = async (responseId: string) => {
        try {
            await apiRequest(`/admin/support/canned-responses/${responseId}`, { method: 'DELETE' });
            await load();
        } catch {
            setError('Failed to delete');
        }
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>Canned Responses ({responses.length})</Typography>
                <Button variant="contained" size="small" startIcon={<Add />}
                    sx={{ backgroundColor: TEAL }} onClick={() => setDialogOpen(true)}>
                    New Template
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress /></Box>
            ) : responses.length === 0 ? (
                <Alert severity="info">No canned responses yet. Create one to speed up support replies.</Alert>
            ) : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell><strong>Title</strong></TableCell>
                                <TableCell><strong>Category</strong></TableCell>
                                <TableCell><strong>Created By</strong></TableCell>
                                <TableCell><strong>Preview</strong></TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {responses.map(r => (
                                <TableRow key={r.response_id}>
                                    <TableCell><Typography variant="body2" fontWeight={500}>{r.title}</Typography></TableCell>
                                    <TableCell>{r.category ? <Chip label={r.category} size="small" /> : '—'}</TableCell>
                                    <TableCell><Typography variant="caption">{r.created_by_name || '—'}</Typography></TableCell>
                                    <TableCell>
                                        <Tooltip title={r.body}>
                                            <Typography variant="caption" sx={{ maxWidth: 200, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {r.body}
                                            </Typography>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <IconButton size="small" color="error" onClick={() => deleteResponse(r.response_id)}>
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>New Canned Response</DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <TextField fullWidth size="small" label="Title" value={newTitle}
                        onChange={e => setNewTitle(e.target.value)} sx={{ mb: 2, mt: 1 }} />
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                        <InputLabel>Category (optional)</InputLabel>
                        <Select value={newCategory} label="Category (optional)" onChange={e => setNewCategory(e.target.value)}>
                            <MenuItem value="">None</MenuItem>
                            <MenuItem value="bug">Bug</MenuItem>
                            <MenuItem value="billing">Billing</MenuItem>
                            <MenuItem value="account">Account</MenuItem>
                            <MenuItem value="trip">Trip</MenuItem>
                            <MenuItem value="feature_request">Feature Request</MenuItem>
                            <MenuItem value="other">Other</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField fullWidth multiline rows={5} size="small" label="Response body"
                        value={newBody} onChange={e => setNewBody(e.target.value)} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={create} disabled={saving || !newTitle.trim() || !newBody.trim()}
                        sx={{ backgroundColor: TEAL }}>
                        {saving ? 'Saving...' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

// ── Sub-tab: SLA Dashboard ─────────────────────────────────────────────────────

const SlaDashboardTab: React.FC = () => {
    const [summary, setSummary] = useState<SlaSummary | null>(null);
    const [breached, setBreached] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const res = await apiRequest(`/admin/support/sla-status`);
            const data = await res.json();
            if (data.success) {
                setSummary(data.summary);
                setBreached(data.breached_tickets);
            } else {
                setError(data.error || 'Failed to load SLA data');
            }
        } catch {
            setError('Network error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const statCard = (label: string, value: string | number, color?: string) => (
        <Card variant="outlined" sx={{ flex: 1, minWidth: 140 }}>
            <CardContent sx={{ textAlign: 'center', py: 2, '&:last-child': { pb: 2 } }}>
                <Typography variant="h4" fontWeight={700} color={color || 'text.primary'}>{value}</Typography>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
            </CardContent>
        </Card>
    );

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600}>SLA Dashboard</Typography>
                <Button size="small" variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}><CircularProgress /></Box>
            ) : summary ? (
                <>
                    <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                        {statCard('Open Tickets', summary.open_count)}
                        {statCard('SLA Breached', summary.breached_count, Number(summary.breached_count) > 0 ? 'error.main' : 'success.main')}
                        {statCard('Urgent Open', summary.urgent_open, Number(summary.urgent_open) > 0 ? 'error.main' : 'text.primary')}
                        {statCard('High Open', summary.high_open, Number(summary.high_open) > 0 ? 'warning.main' : 'text.primary')}
                        {statCard('Avg First Response (hrs)', summary.avg_first_response_hours ?? '—')}
                        {statCard('Awaiting First Response', summary.awaiting_first_response, Number(summary.awaiting_first_response) > 0 ? 'warning.main' : 'text.primary')}
                    </Box>

                    {breached.length > 0 && (
                        <>
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {breached.length} ticket{breached.length > 1 ? 's have' : ' has'} breached SLA and require{breached.length === 1 ? 's' : ''} immediate attention.
                            </Alert>
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow sx={{ backgroundColor: '#fff3f3' }}>
                                            <TableCell><strong>Subject</strong></TableCell>
                                            <TableCell><strong>User</strong></TableCell>
                                            <TableCell><strong>Priority</strong></TableCell>
                                            <TableCell><strong>SLA Target</strong></TableCell>
                                            <TableCell><strong>Created</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {breached.map(t => (
                                            <TableRow key={t.ticket_id}>
                                                <TableCell><Typography variant="body2">{t.subject}</Typography></TableCell>
                                                <TableCell><Typography variant="caption">{t.user_email}</Typography></TableCell>
                                                <TableCell><Chip label={t.priority} color={priorityColor(t.priority)} size="small" /></TableCell>
                                                <TableCell><Typography variant="caption" color="error">{fmtDate(t.sla_target)}</Typography></TableCell>
                                                <TableCell><Typography variant="caption">{fmtDateShort(t.created_at)}</Typography></TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </>
                    )}
                    {breached.length === 0 && (
                        <Alert severity="success" icon={<CheckCircle />}>All tickets are within SLA targets.</Alert>
                    )}
                </>
            ) : null}
        </Box>
    );
};

// ── Main Component ─────────────────────────────────────────────────────────────

const SupportTicketsTab: React.FC = () => {
    const [subTab, setSubTab] = useState(0);

    return (
        <Box>
            <Typography variant="h5" fontWeight={600} gutterBottom>Support</Typography>

            <Tabs value={subTab} onChange={(_, v) => setSubTab(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
                <Tab label="Queue" icon={<ConfirmationNumber />} iconPosition="start" />
                <Tab label="Contact Inquiries" icon={<Mail />} iconPosition="start" />
                <Tab label="Canned Responses" icon={<Reply />} iconPosition="start" />
                <Tab label="SLA Dashboard" icon={<Schedule />} iconPosition="start" />
            </Tabs>

            {subTab === 0 && <QueueTab />}
            {subTab === 1 && <InquiriesTab />}
            {subTab === 2 && <CannedResponsesTab />}
            {subTab === 3 && <SlaDashboardTab />}
        </Box>
    );
};

export default SupportTicketsTab;